import { request } from '../lib/api';

/**
 * 简化的文件上传配置
 */
export interface SimpleUploadConfig {
  // 文件类型：document, avatar, voice, other
  fileType: 'document' | 'avatar' | 'voice' | 'other';
  // 最大文件大小(MB)
  maxSize?: number;
  // 业务上下文
  businessContext?: {
    user_id?: string;
    knowledge_base_id?: string;
    custom_path?: string; // 自定义文件存储路径
    [key: string]: any;
  };
}

/**
 * 上传结果
 */
export interface UploadResult {
  success: boolean;
  fileUrl?: string;
  fileKey?: string;
  fileName?: string;
  fileSize?: number;
  error?: string;
}

/**
 * 简化的文件上传函数
 * @param file 要上传的文件
 * @param config 上传配置
 * @param onProgress 进度回调
 * @returns Promise<UploadResult>
 */
export const uploadFile = async (
  file: File,
  config: SimpleUploadConfig,
  onProgress?: (percent: number) => void
): Promise<UploadResult> => {
  try {
    // 文件大小检查
    const sizeLimit = getSizeLimit(config.fileType, config.maxSize);
    const fileSizeMB = file.size / 1024 / 1024;
    
    if (fileSizeMB > sizeLimit) {
      return {
        success: false,
        error: `文件大小不能超过 ${sizeLimit}MB`
      };
    }

    // 第一步：获取上传Token
    const tokenResponse = await request('/storage/upload-token', {
      method: 'POST',
      data: {
        file_type: config.fileType,
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        file_size: file.size,
        business_context: config.businessContext,
      },
    });

    if (!tokenResponse.success) {
      return {
        success: false,
        error: tokenResponse.errorMessage || '获取上传Token失败'
      };
    }

    const { upload_url, file_key, upload_token } = tokenResponse.data;

    // 第二步：使用预签名URL上传到MinIO
    const uploadResult = await uploadToMinio(file, upload_url, onProgress);
    
    if (!uploadResult.success) {
      return uploadResult;
    }

    // 第三步：提交文件信息
    const commitResponse = await request('/storage/commit', {
      method: 'POST',
      data: {
        upload_token,
        file_key,
        filename: file.name,
        file_size: file.size,
        content_type: file.type || 'application/octet-stream',
        business_context: config.businessContext,
      },
    });

    if (!commitResponse.success) {
      return {
        success: false,
        error: commitResponse.errorMessage || '提交文件失败'
      };
    }

    const fileInfo = commitResponse.data;
    return {
      success: true,
      fileUrl: fileInfo.file_url,
      fileKey: fileInfo.file_key,
      fileName: file.name,
      fileSize: file.size
    };

  } catch (error) {
    console.error('文件上传失败:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '上传失败'
    };
  }
};

/**
 * 上传到MinIO
 */
const uploadToMinio = (
  file: File,
  uploadUrl: string,
  onProgress?: (percent: number) => void
): Promise<UploadResult> => {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    
    // 监听上传进度
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded * 100) / event.total);
        onProgress(percent);
      }
    });

    // 监听上传完成
    xhr.addEventListener('load', () => {
      if (xhr.status === 200 || xhr.status === 204) {
        resolve({ success: true });
      } else {
        resolve({
          success: false,
          error: `上传失败: ${xhr.statusText}`
        });
      }
    });

    // 监听上传错误
    xhr.addEventListener('error', () => {
      resolve({
        success: false,
        error: '网络错误'
      });
    });

    // 发送请求到MinIO预签名URL
    xhr.open('PUT', uploadUrl);
    xhr.send(file);
  });
};

/**
 * 获取文件大小限制
 */
const getSizeLimit = (fileType: string, customSize?: number): number => {
  if (customSize) return customSize;
  
  const sizeMap: Record<string, number> = {
    document: 50, // 50MB
    avatar: 5,    // 5MB
    voice: 20,    // 20MB
    other: 100,   // 100MB
  };
  
  return sizeMap[fileType] || 100;
};

/**
 * 批量上传文件
 * @param files 文件列表
 * @param config 上传配置
 * @param onProgress 总体进度回调
 * @returns Promise<UploadResult[]>
 */
export const uploadFiles = async (
  files: File[],
  config: SimpleUploadConfig,
  onProgress?: (percent: number) => void
): Promise<UploadResult[]> => {
  const results: UploadResult[] = [];
  let completedCount = 0;
  
  for (const file of files) {
    const result = await uploadFile(file, config, (filePercent) => {
      // 计算总体进度
      const totalPercent = Math.round(
        ((completedCount + filePercent / 100) / files.length) * 100
      );
      onProgress?.(totalPercent);
    });
    
    results.push(result);
    completedCount++;
    
    // 更新总体进度
    onProgress?.(Math.round((completedCount / files.length) * 100));
  }
  
  return results;
};